import { tap } from 'rxjs';

import { Injectable } from '@angular/core';

import { patch, updateItem } from '@ngxs/store/operators';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';

import { assertArray } from '@app/core/utilities/array.utilities';
import { CustomerState } from '@app/core/store/states/customer/customer.state';
import { AlertsApiService } from '@app/core/alerts-api.service';
import { ApolloMutePayloads } from '@app/settings/user/modules/alert-settings/alert-settings.utilities';
import { getActionFromSource } from '@app/alerts/utilities/alerts.utilities';
import { ApiStoreConnectService } from '@app/core/store/services/api-store-connect.service';
import { AlertSettingsApiService } from '@app/settings/user/modules/alert-settings/alert-settings-api.service';
import { AlertsApiRoute, ApiEndpoint } from '@app/core/models/api.models';
import { Alert, ALERT_STATE, AlertsStateModel } from '@app/alerts/state/alerts-state.models';
import { ApiListEmit, ApiListEmitAction, ApiListRefresh } from '@app/core/store/models/api-action.models';
import {
  GetAlertCategories,
  GetAlertsInit,
  GetDrawerAlerts,
  GetPageAlerts,
  GetUnreadAlertsCount,
  MuteAlert,
  SetAlertRead,
  SetAlertResolved,
} from '@app/alerts/state/alerts-state.actions';

type StateCtx = StateContext<AlertsStateModel>;

@State({
  name: ALERT_STATE,
  defaults: {
    alerts: {},
  },
})
@Injectable()
export class AlertsState {
  @Selector()
  static unreadCount({ unreadCount }: AlertsStateModel) {
    return unreadCount || 0;
  }

  @Selector()
  static alerts({ alerts }: AlertsStateModel) {
    return alerts;
  }

  @Selector()
  static categories({ categories }: AlertsStateModel) {
    return assertArray(categories);
  }

  constructor(
    private asc: ApiStoreConnectService,
    private asa: AlertSettingsApiService,
    private store: Store,
    private aa: AlertsApiService,
  ) {}

  @Action(GetAlertsInit)
  getAlertsInit({ setState, dispatch }: StateCtx, { source }: GetAlertsInit) {
    setState(patch({ alerts: patch({ [source]: [] }) }));

    const customerId = this.store.selectSnapshot(CustomerState.customerId);
    const action = getActionFromSource(source);
    const sortValues =
      source === 'drawer' ? { cleared: 1, severity: -2, createdAt: -3 } : { cleared: 1, severity: -2, createdAt: -3 };

    this.asc.connectList<Alert>(action, {
      init: {
        endpoint: ApiEndpoint.Notifications,
        route: [AlertsApiRoute.Instances],
        fixedFilterValues: {
          customerId,
          muted: false,
        },
        filterValues: {
          cleared: [false],
        },
        sortValues,
        limit: source === 'page' ? 5 : 10,
      },
    });

    dispatch(new action());
  }

  @Action(GetUnreadAlertsCount)
  getUnreadAlertsCount({ patchState }: StateCtx) {
    const customerId = this.store.selectSnapshot(CustomerState.customerId);

    return this.aa.getUnreadAlertsCount(customerId).pipe(tap((unreadCount) => patchState({ unreadCount })));
  }

  @Action(GetAlertCategories)
  getAlertCategories({ patchState, getState }: StateCtx) {
    if (getState().categories?.length) {
      return null;
    }

    return this.asa.getCategories().pipe(tap((categories) => patchState({ categories })));
  }

  @Action(ApiListEmitAction<Alert>(GetDrawerAlerts))
  getDrawerAlerts({ setState }: StateCtx, { response }: ApiListEmit<Alert>) {
    setState(patch({ alerts: patch({ drawer: response.data }) }));
  }

  @Action(ApiListEmitAction<Alert>(GetPageAlerts))
  getPageAlerts({ setState }: StateCtx, { response }: ApiListEmit<Alert>) {
    setState(patch({ alerts: patch({ page: response.data }) }));
  }

  @Action(SetAlertRead)
  setAlertRead({ setState, dispatch, getState }: StateCtx, { alertId, read }: SetAlertRead) {
    this.patchAlert({ setState, getState }, alertId, { read });

    return this.aa.updateAlerts([{ instanceId: alertId, read }]).pipe(tap(() => dispatch(new GetUnreadAlertsCount())));
  }

  @Action(SetAlertResolved)
  setAlertResolved({ setState, dispatch, getState }: StateCtx, { alertId, cleared }: SetAlertResolved) {
    this.patchAlert({ setState, getState }, alertId, { cleared });

    return this.aa
      .updateAlerts([{ instanceId: alertId, cleared }])
      .pipe(tap(() => dispatch(new GetUnreadAlertsCount())));
  }

  @Action(MuteAlert)
  muteAlert({ dispatch }: StateCtx, { definitionId, muteType }: MuteAlert) {
    const { deletes, edits } = ApolloMutePayloads([{ id: definitionId, muteType }]);

    return this.asa
      .updateMutes(deletes, edits)
      .pipe(tap(() => dispatch([new ApiListRefresh(GetDrawerAlerts), new ApiListRefresh(GetPageAlerts)])));
  }

  private patchAlert({ setState, getState }: Partial<StateCtx>, alertId: number, update: Partial<Alert>) {
    const { drawer, page } = getState().alerts;

    setState(
      patch({
        alerts: patch({ drawer: drawer ? updateItem((a) => a.instanceId === alertId, patch(update)) : [] }),
      }),
    );
    setState(
      patch({ alerts: patch({ page: page ? updateItem((a) => a.instanceId === alertId, patch(update)) : [] }) }),
    );
  }
}
